<template>
  <div class="email-verified-container">
    <!-- 🔥 Экран загрузки -->
    <div v-if="isVerifying" class="loading-overlay">
      <div class="spinner"></div>
      <p class="loading-text">Проверяем ваш email...</p>
    </div>

    <div v-else class="verified-content">
      <template v-if="errorMessage">
        <div class="error-box">
          <h3 class="error-title">Ошибка подтверждения</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="retryVerification">Попробовать снова</button>
        </div>
      </template>

      <template v-else>
        <div class="success-box">
          <img src="../assets/verified_icon.png" alt="Успешно" class="success-icon" />
          <h2 class="success-title">Email подтверждён!</h2>
          <p class="success-text">Вы будете перенаправлены...</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>

import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { getAuth, applyActionCode, signInWithEmailAndPassword } from "firebase/auth";
import EmailConfirmationService from "../api/EmailConfirmationService.js";

const route = useRoute();
const isVerifying = ref(true);
const errorMessage = ref("");

onMounted(async () => {

  const oobCode = route.query.oobCode;
  const email = extractEmailFromContinueUrl() || localStorage.getItem("emailForSignIn");
  const password = localStorage.getItem("user_password");

  console.log("📩 Начинаем подтверждение email...");
  console.log("🧐 Полученный oobCode:", oobCode);
  console.log("📧 Извлечённый email:", email || "❌ Email отсутствует!");
  console.log("🔑 Извлечённый пароль:", password);

  if (!oobCode) {
    errorMessage.value = "❌ Ошибка: Код подтверждения отсутствует.";
    isVerifying.value = false;
    return;
  }

  try {
    const auth = getAuth();

    // ✅ Подтверждаем email через Firebase
    await applyActionCode(auth, oobCode);
    console.log("✅ Email успешно подтверждён!");

  } catch (error) {
    console.error("❌ Ошибка при подтверждении email:", error);

    if (error.code === "auth/invalid-action-code") {
      errorMessage.value = "⛔ Код подтверждения недействителен или уже использован.";
    } else {
      errorMessage.value = error.message || "Ошибка при подтверждении email.";
    }
    } finally {
      isVerifying.value = false;

      if (email) {
        try {
          await EmailConfirmationService.markAsVerified(email);
        } catch (e) {
          console.error("❌ Ошибка при обновлении verified в Firestore:", e);
        }
      }
    }
});

const retryVerification = () => {
  errorMessage.value = "";
  isVerifying.value = true;
  setTimeout(() => window.location.reload(), 1000);
};

const extractEmailFromContinueUrl = () => {
  const rawOuter = route.query.continueUrl;
  if (!rawOuter) return null;

  try {
    // 🔓 Первый decode
    const level1 = decodeURIComponent(rawOuter);
    const level1Url = new URL(level1);
    const innerEncoded = level1Url.searchParams.get("continueUrl");

    if (!innerEncoded) return null;

    // 🔓 Второй decode
    const level2 = decodeURIComponent(innerEncoded);
    const level2Url = new URL(level2);
    const rawEmail = level2Url.searchParams.get("email");

    return rawEmail ? decodeURIComponent(rawEmail) : null;
  } catch (e) {
    console.error("❌ Ошибка при разборе continueUrl:", e);
    return null;
  }
};

</script>

<style scoped>
/* 🔥 Оверлей загрузки */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: white;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 16px;
  margin-top: 15px;
}

/* ✅ Основной контейнер */
.email-verified-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #161616;
}

/* ✅ Контент */
.verified-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ✅ Ошибки */
.error-box {
  background-color: #2B2B2B;
  padding: 25px;
  border-radius: 10px;
  width: 350px;
  text-align: center;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.3);
}

.error-title {
  font-size: 18px;
  font-family: "Eastman-Bold", sans-serif;
  color: white;
  margin-bottom: 10px;
}

.error-message {
  color: #E63946;
  font-size: 14px;
  margin-bottom: 15px;
}

.retry-button {
  background: #E63946;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.2s ease-in-out;
}

.retry-button:hover {
  background: #C62828;
}

/* ✅ Успешное подтверждение */
.success-box {
  background-color: #1F1F1F;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.3);
}

.success-icon {
  width: 80px;
  margin-bottom: 15px;
}

.success-title {
  font-size: 22px;
  font-family: "Eastman-Bold", sans-serif;
  color: #FFFFFF;
  margin-bottom: 10px;
}

.success-text {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: #CCCCCC;
  line-height: 1.5;
}
</style>